import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, MoreVertical, Pencil, Trash2, Plus } from "lucide-react";
import { StepRow } from "./step-row";
import { StepDialog } from "./step-dialog";
import type { TourAudienceCriteria, TourRecord, TourStepRecord } from "./types";

interface TourCardProps {
  tour: TourRecord;
  audienceId: string;
  audienceCriteria: TourAudienceCriteria;
  onEdit: (t: TourRecord) => void;
}

export function TourCard({ tour, audienceId, audienceCriteria, onEdit }: TourCardProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [stepDialogOpen, setStepDialogOpen] = useState(false);
  const [editingStep, setEditingStep] = useState<TourStepRecord | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const stepsQuery = useQuery<{ steps: TourStepRecord[] }>({
    queryKey: ["/api/admin/tours", tour.id, "steps"],
    enabled: open,
  });

  const activateMutation = useMutation({
    mutationFn: (next: boolean) =>
      apiRequest("PATCH", `/api/admin/tours/${tour.id}`, { isActive: next }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/tours", audienceId] });
    },
    onError: (err: Error) =>
      toast({ title: "Update failed", description: err.message, variant: "destructive" }),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiRequest("DELETE", `/api/admin/tours/${tour.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/admin/tours", audienceId] });
      toast({ title: "Tour deleted" });
      setConfirmDelete(false);
    },
    onError: (err: Error) =>
      toast({ title: "Delete failed", description: err.message, variant: "destructive" }),
  });

  const steps = stepsQuery.data?.steps ?? [];

  function openNewStep() {
    setEditingStep(null);
    setStepDialogOpen(true);
  }

  function openEditStep(s: TourStepRecord) {
    setEditingStep(s);
    setStepDialogOpen(true);
  }

  return (
    <Card data-testid={`card-tour-${tour.id}`}>
      <Collapsible open={open} onOpenChange={setOpen}>
        <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-3">
          <div className="flex items-center gap-2 min-w-0">
            <CollapsibleTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                aria-label={open ? "Collapse steps" : "Expand steps"}
                data-testid={`button-toggle-tour-${tour.id}`}
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
              </Button>
            </CollapsibleTrigger>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold truncate" data-testid={`text-tour-title-${tour.id}`}>
                  {tour.title}
                </span>
                <Badge variant="outline" className="font-mono text-[10px]">
                  {tour.routePattern}
                </Badge>
                {tour.isActive ? (
                  <Badge>Active</Badge>
                ) : (
                  <Badge variant="secondary">Draft</Badge>
                )}
              </div>
              {tour.description && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                  {tour.description}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Switch
              checked={tour.isActive}
              onCheckedChange={(v) => activateMutation.mutate(v)}
              disabled={activateMutation.isPending}
              data-testid={`switch-tour-active-${tour.id}`}
              aria-label={tour.isActive ? "Deactivate tour" : "Activate tour"}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="ghost" data-testid={`button-tour-actions-${tour.id}`}>
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => onEdit(tour)} data-testid={`menu-edit-tour-${tour.id}`}>
                  <Pencil className="w-4 h-4 mr-2" /> Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setConfirmDelete(true)}
                  className="text-destructive"
                  data-testid={`menu-delete-tour-${tour.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CollapsibleContent>
          <CardContent className="space-y-2 pt-0">
            {stepsQuery.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-16 w-full" />
                ))}
              </div>
            ) : (
              <>
                {steps.map((s, i) => (
                  <StepRow
                    key={s.id}
                    step={s}
                    tourId={tour.id}
                    index={i}
                    total={steps.length}
                    audienceCriteria={audienceCriteria}
                    onEdit={openEditStep}
                  />
                ))}
                {steps.length === 0 && (
                  <p className="text-sm text-muted-foreground py-4 text-center">
                    No steps yet.
                  </p>
                )}
              </>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={openNewStep}
              data-testid={`button-add-step-${tour.id}`}
            >
              <Plus className="w-4 h-4 mr-2" /> Add step
            </Button>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
      <StepDialog
        open={stepDialogOpen}
        onOpenChange={setStepDialogOpen}
        tourId={tour.id}
        tourRoutePattern={tour.routePattern}
        audienceCriteria={audienceCriteria}
        step={editingStep}
      />
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tour?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the tour and all its steps. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); deleteMutation.mutate(); }}
              className="bg-destructive text-destructive-foreground"
              data-testid={`button-confirm-delete-tour-${tour.id}`}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
